import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    ArrowLeft, ChartLine, DownloadSimple, Funnel, ChartBar,
    Clock, Globe, Users, Briefcase, Handshake,
} from "@phosphor-icons/react";
import { SegmentedControl } from "@/components/admin/SegmentedControl";

/* ============================================================================
   AdminLeadsReports — 5 reports for the Leads Hub
   ----------------------------------------------------------------------------
       1. Pipeline snapshot — what's open right now, by kind/status/assignee
       2. Velocity — created/closed/won per week per kind
       3. SLA performance — per-assignee SLA met %, median TTFT
       4. Source quality (web leads) — conversion rate by source
       5. Assignee scorecard — handled / win rate / SLA met % / avg cycle

   Each report:
       - Date range picker (presets: 7d, 30d, 90d, all-time, custom)
       - Summary KPI strip
       - Drill-down table
       - CSV download (client-side generation from the JSON rows)
   ========================================================================== */

const REPORTS = [
    { id: "pipeline",            label: "Pipeline snapshot", Icon: Funnel,    desc: "Open leads by kind, status, and assignee — where everything sits right now." },
    { id: "velocity",            label: "Velocity",          Icon: ChartLine, desc: "Created vs closed vs won, week-over-week across web / B2B / applications." },
    { id: "sla",                 label: "SLA performance",   Icon: Clock,     desc: "Per-assignee handling: breached, met, median time-to-first-touch." },
    { id: "source-quality",      label: "Source quality",    Icon: Globe,     desc: "Web leads grouped by traffic source with conversion rate." },
    { id: "assignee-scorecard",  label: "Assignee scorecard",Icon: Users,     desc: "Per-rep: handled, win rate, SLA met %, avg cycle hours." },
];

const PRESETS = [
    { id: "7d",    label: "Last 7 days",  days: 7 },
    { id: "30d",   label: "Last 30 days", days: 30 },
    { id: "90d",   label: "Last 90 days", days: 90 },
    { id: "365d",  label: "Last year",    days: 365 },
    { id: "all",   label: "All time",     days: 3650 },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function AdminLeadsReports() {
    const [reportId, setReportId] = useState("pipeline");
    const [preset, setPreset] = useState("30d");
    const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
    const [dateTo, setDateTo] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const applyPreset = (p) => {
        setPreset(p);
        const def = PRESETS.find((x) => x.id === p);
        if (def) {
            setDateFrom(daysAgoISO(def.days));
            setDateTo(todayISO());
        }
    };

    useEffect(() => {
        setLoading(true);
        api.get(`/admin/leads-hub/reports/${reportId}`, {
            params: { date_from: dateFrom + "T00:00:00", date_to: dateTo + "T23:59:59" },
        }).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [reportId, dateFrom, dateTo]);

    const downloadCSV = () => {
        if (!data?.rows?.length) return;
        const rows = data.rows;
        const headers = Object.keys(rows[0]);
        const csv = [
            headers.join(","),
            ...rows.map((r) => headers.map((h) => csvCell(r[h])).join(",")),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `invisique-${reportId}-${dateFrom}-to-${dateTo}.csv`;
        link.click();
    };

    const current = REPORTS.find((r) => r.id === reportId) || REPORTS[0];

    return (
        <div className="space-y-5" data-testid="admin-leads-reports">
            <Link to="/admin/leads" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="reports-back-link">
                <ArrowLeft size={14} /> Back to leads inbox
            </Link>

            <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
                    <div>
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Leads</div>
                        <h1 className="font-display text-3xl font-bold tracking-tight mt-0.5 flex items-center gap-2">
                            <ChartBar size={26} weight="duotone" /> Reports
                        </h1>
                        <p className="text-muted-foreground mt-1 text-sm max-w-xl">
                            Slice the pipeline by date, kind, and assignee. Every report exports to CSV for sharing or follow-up automation.
                        </p>
                    </div>
                    <Button onClick={downloadCSV} disabled={!data?.rows?.length} className="rounded-full"
                        data-testid="reports-download-csv">
                        <DownloadSimple size={14} className="mr-1.5" /> Download CSV
                    </Button>
                </div>
            </div>

            {/* Report selector */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {REPORTS.map((r) => {
                    const on = reportId === r.id;
                    return (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() => setReportId(r.id)}
                            className={`text-left p-3 sm:p-4 rounded-xl border transition-all ${
                                on
                                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                    : "border-border bg-card hover:border-primary/40"
                            }`}
                            data-testid={`report-card-${r.id}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <r.Icon size={16} weight="duotone" className={on ? "text-primary" : "text-muted-foreground"} />
                                <div className="font-semibold text-sm">{r.label}</div>
                            </div>
                            <div className="text-[11px] text-muted-foreground line-clamp-2">{r.desc}</div>
                        </button>
                    );
                })}
            </div>

            {/* Date range controls */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4 bg-card border border-border rounded-2xl p-4">
                <SegmentedControl
                    label="Range"
                    value={preset}
                    onChange={applyPreset}
                    options={PRESETS.map((p) => ({ value: p.id, label: p.label.replace(/^Last /, "") }))}
                    testid="report-preset"
                />
                <div className="flex items-end gap-2">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">From</Label>
                        <Input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setPreset(""); }}
                            className="h-9"
                            data-testid="report-date-from"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">To</Label>
                        <Input
                            type="date"
                            value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setPreset(""); }}
                            className="h-9"
                            data-testid="report-date-to"
                        />
                    </div>
                </div>
            </div>

            {/* Report body */}
            <div className="bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="report-body">
                <div className="flex items-center gap-2 mb-4">
                    <current.Icon size={18} weight="duotone" className="text-primary" />
                    <h2 className="font-display text-xl font-bold tracking-tight">{current.label}</h2>
                    {data?.range && (
                        <span className="text-[10px] text-muted-foreground tracking-wide uppercase ml-auto">
                            {data.range.from.slice(0, 10)} → {data.range.to.slice(0, 10)}
                        </span>
                    )}
                </div>
                {loading ? (
                    <div className="text-muted-foreground text-sm text-center py-10">Loading…</div>
                ) : !data || !data.rows?.length ? (
                    <div className="text-muted-foreground text-sm text-center py-10" data-testid="report-empty">
                        No data in this range. Try widening the date filter.
                    </div>
                ) : (
                    <ReportBody reportId={reportId} data={data} />
                )}
            </div>
        </div>
    );
}

/* ============================================================================
   Per-report body renderers — KPI cards + table
   ========================================================================== */

function ReportBody({ reportId, data }) {
    if (reportId === "pipeline") return <PipelineReport data={data} />;
    if (reportId === "velocity") return <VelocityReport data={data} />;
    if (reportId === "sla") return <SLAReport data={data} />;
    if (reportId === "source-quality") return <SourceQualityReport data={data} />;
    if (reportId === "assignee-scorecard") return <AssigneeScorecard data={data} />;
    return null;
}

function KPI({ label, value, sub, tone }) {
    const cls = tone === "emerald" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
        : tone === "rose" ? "text-rose-700 bg-rose-50 border-rose-200"
        : tone === "amber" ? "text-amber-700 bg-amber-50 border-amber-200"
        : "text-primary bg-primary/5 border-primary/20";
    return (
        <div className={`rounded-xl border p-3 ${cls}`}>
            <div className="text-2xl font-display font-bold tabular-nums leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold mt-1">{label}</div>
            {sub && <div className="text-[11px] opacity-80 mt-0.5">{sub}</div>}
        </div>
    );
}

function StyledTable({ headers, rows, testid }) {
    return (
        <div className="rounded-xl border border-border overflow-x-auto" data-testid={testid}>
            <table className="w-full text-sm">
                <thead className="bg-secondary/40">
                    <tr>
                        {headers.map((h) => (
                            <th key={h.key} className="px-3 py-2 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground text-left">
                                {h.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-t border-border hover:bg-secondary/20 transition">
                            {headers.map((h) => (
                                <td key={h.key} className={`px-3 py-2 ${h.align === "right" ? "text-right tabular-nums" : ""}`}>
                                    {h.render ? h.render(r[h.key], r) : r[h.key] ?? "—"}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const KIND_LABELS = { web: "Web", b2b: "B2B", application: "App" };
const KIND_ICONS = { web: Globe, b2b: Handshake, application: Briefcase };

function KindBadge({ kind }) {
    const Icon = KIND_ICONS[kind] || Globe;
    return (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-secondary text-foreground">
            <Icon size={10} weight="fill" /> {KIND_LABELS[kind] || kind}
        </span>
    );
}

/* ---------- Pipeline ---------- */
function PipelineReport({ data }) {
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KPI label="Total" value={data.summary.total} />
                <KPI label="Open" value={data.summary.open} tone="emerald" />
                <KPI label="Closed" value={data.summary.closed} />
                <KPI label="Unassigned" value={data.summary.unassigned} tone="amber" />
            </div>

            <div>
                <h3 className="text-sm font-semibold mb-2">By kind & status</h3>
                <StyledTable
                    testid="report-pipeline-table"
                    headers={[
                        { key: "kind",   label: "Kind",   render: (v) => <KindBadge kind={v} /> },
                        { key: "status", label: "Status", render: (v) => <span className="font-mono uppercase text-[11px] tracking-wide">{(v || "").replace(/_/g, " ")}</span> },
                        { key: "count",  label: "Count",  align: "right" },
                    ]}
                    rows={data.rows}
                />
            </div>

            {data.by_assignee?.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold mb-2">Open leads by assignee</h3>
                    <StyledTable
                        testid="report-pipeline-by-assignee"
                        headers={[
                            { key: "assignee_name", label: "Owner" },
                            { key: "assignee_role", label: "Role" },
                            { key: "web",           label: "Web", align: "right" },
                            { key: "b2b",           label: "B2B", align: "right" },
                            { key: "application",   label: "App", align: "right" },
                            { key: "total",         label: "Total", align: "right" },
                        ]}
                        rows={data.by_assignee}
                    />
                </div>
            )}
        </div>
    );
}

/* ---------- Velocity ---------- */
function VelocityReport({ data }) {
    const kinds = ["web", "b2b", "application"];
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {kinds.map((k) => {
                    const s = data.summary[k] || {};
                    return (
                        <div key={k} className="rounded-xl border border-border bg-secondary/30 p-3">
                            <div className="flex items-center gap-2 mb-1">
                                <KindBadge kind={k} />
                                <span className="text-xs text-muted-foreground">win rate</span>
                                <span className="ml-auto text-2xl font-display font-bold tabular-nums leading-none">
                                    {s.win_rate ?? 0}%
                                </span>
                            </div>
                            <div className="text-[11px] text-muted-foreground tabular-nums">
                                {s.created ?? 0} created · {s.closed ?? 0} closed · {s.won ?? 0} won
                            </div>
                        </div>
                    );
                })}
            </div>

            <StyledTable
                testid="report-velocity-table"
                headers={[
                    { key: "week_start", label: "Week of", render: (v) => v },
                    ...kinds.flatMap((k) => [
                        { key: `${k}_created`, label: `${KIND_LABELS[k]} created`, align: "right" },
                        { key: `${k}_closed`,  label: `${KIND_LABELS[k]} closed`,  align: "right" },
                        { key: `${k}_won`,     label: `${KIND_LABELS[k]} won`,     align: "right" },
                    ]),
                ]}
                rows={data.rows}
            />
        </div>
    );
}

/* ---------- SLA performance ---------- */
function SLAReport({ data }) {
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KPI label="Handled" value={data.summary.handled} />
                <KPI label="Met" value={data.summary.met} tone="emerald" />
                <KPI label="Missed" value={data.summary.missed} tone="rose" />
                <KPI label="Breached (open)" value={data.summary.breached} tone="rose" />
            </div>

            <StyledTable
                testid="report-sla-table"
                headers={[
                    { key: "assignee_name", label: "Owner" },
                    { key: "assignee_role", label: "Role" },
                    { key: "handled",       label: "Handled", align: "right" },
                    { key: "met",           label: "Met",     align: "right" },
                    { key: "missed",        label: "Missed",  align: "right" },
                    { key: "sla_met_pct",   label: "Met %",   align: "right", render: (v) => `${v}%` },
                    {
                        key: "median_first_touch_hours",
                        label: "Median TTFT",
                        align: "right",
                        render: (v) => v != null ? `${v}h` : "—",
                    },
                ]}
                rows={data.rows}
            />
        </div>
    );
}

/* ---------- Source quality (web) ---------- */
function SourceQualityReport({ data }) {
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KPI label="Total web" value={data.summary.total} />
                <KPI label="Converted" value={data.summary.converted} tone="emerald" />
                <KPI label="Lost" value={data.summary.lost} tone="rose" />
                <KPI label="Open" value={data.summary.open} tone="amber" />
            </div>

            <StyledTable
                testid="report-source-table"
                headers={[
                    { key: "source",          label: "Source" },
                    { key: "total",           label: "Total",     align: "right" },
                    { key: "converted",       label: "Converted", align: "right" },
                    { key: "lost",            label: "Lost",      align: "right" },
                    { key: "open",            label: "Open",      align: "right" },
                    { key: "conversion_pct",  label: "Conv %",    align: "right", render: (v) => `${v}%` },
                    { key: "avg_score",       label: "Avg score", align: "right", render: (v) => v ?? "—" },
                ]}
                rows={data.rows}
            />
        </div>
    );
}

/* ---------- Assignee scorecard ---------- */
function AssigneeScorecard({ data }) {
    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KPI label="Handled" value={data.summary.handled} />
                <KPI label="Won" value={data.summary.won} tone="emerald" />
                <KPI label="Lost" value={data.summary.lost} tone="rose" />
                <KPI label="Open" value={data.summary.open} tone="amber" />
            </div>

            <StyledTable
                testid="report-assignee-table"
                headers={[
                    { key: "assignee_name",     label: "Owner" },
                    { key: "assignee_role",     label: "Role" },
                    { key: "handled",           label: "Handled", align: "right" },
                    { key: "won",               label: "Won",     align: "right" },
                    { key: "lost",              label: "Lost",    align: "right" },
                    { key: "open",              label: "Open",    align: "right" },
                    { key: "win_rate",          label: "Win %",   align: "right", render: (v) => `${v}%` },
                    { key: "sla_met_pct",       label: "SLA met %", align: "right", render: (v) => `${v}%` },
                    {
                        key: "avg_cycle_hours",
                        label: "Avg cycle",
                        align: "right",
                        render: (v) => v != null ? `${v}h` : "—",
                    },
                ]}
                rows={data.rows}
            />
        </div>
    );
}

/* CSV cell encoder. */
function csvCell(v) {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}
